import { motion } from 'framer-motion';
import { MapPin, Truck, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const surfaceTreatments = [
  'Eloxieren', 'Hartcoatieren', 'Verzinken', 'Vernickeln', 'Brünieren', 'Nitrieren',
  'Verchromen', 'Lackieren'
];

const LocationSection = () => {
  return (
    <section id="standort" className="py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Unser Standort
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Stamsried in der Oberpfalz – kurze Wege, schnelle Ergebnisse
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto items-stretch">
          {/* Google Maps Embed */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl min-h-[400px]"
          >
            <iframe
              src="https://maps.google.com/maps?q=Bernbachstra%C3%9Fe+9,+93491+Stamsried,+Deutschland&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SR-Zerspanung Standort Stamsried"
              className="absolute inset-0 w-full h-full"
            />
          </motion.div>

          {/* Benefits */}
          <div className="flex flex-col gap-6">
            {/* Standort Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-card border border-border rounded-xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-foreground mb-1">
                    Stamsried, Oberpfalz
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Zentral gelegen im Herzen der Oberpfalz – ideale Anbindung an regionale Partner und Zulieferer.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Oberflächenbehandlungen Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-smart/10 flex items-center justify-center shrink-0">
                  <Sparkles className="w-6 h-6 text-smart" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-foreground mb-1">
                    Oberflächenbehandlungen in der Nähe
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    Zahlreiche Veredelungspartner direkt in der Region – für schnelle Durchlaufzeiten.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {surfaceTreatments.map((treatment) => (
                      <Badge key={treatment} variant="secondary" className="text-xs">
                        {treatment}
                      </Badge>
                    ))}
                    <Badge variant="outline" className="text-xs">u.v.m.</Badge>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Eigene Anlieferung Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-fastlane/10 flex items-center justify-center shrink-0">
                  <Truck className="w-6 h-6 text-fastlane" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-foreground mb-1">
                    Eigene Anlieferung & Abholung
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Kein unnötiger Paketversand – wir bringen Ihre Teile persönlich zu unseren Partnern und holen sie wieder ab.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
